// src/frontend_react/src/pages/UserDashboard/PrepareDataPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

// Child Step Components
import ColumnConfigurationStep from './ColumnConfigurationStep';
import ProcessingStep from './ProcessingStep';
import ResultsStep from './ResultsStep';
import TrustAndSecurityCard from '../../components/userDashboard/TrustAndSecurityCard';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Stepper from '../../components/ui/Stepper/Stepper';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Icon from '../../components/ui/Icon/Icon';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';
import Card from '../../components/ui/Card/Card';
import Tooltip from '../../components/ui/Tooltip/Tooltip';

// Local styles
import styles from './PrepareDataPage.module.css';

// API and Utils
import * as api from '../../services/api';
import { parseSelectedFile } from '../../utils/fileParser';
import { calculateFileHash } from '../../utils/cryptoHelper';
import { trackEvent } from '../../utils/analytics';
import { notify } from '../../services/notificationService';

// React Icons
import {
  FiUploadCloud,
  FiFolder,
  FiXCircle,
  FiSettings,
  FiChevronsRight,
  FiSave,
  FiShield,
  FiArrowLeft,
} from 'react-icons/fi';

const STEPS = [
  { id: 'initial_loading', label: 'Initializing', icon: FiSettings },
  { id: 'select_file', label: 'Select File(s)', icon: FiFolder },
  { id: 'configure_columns', label: 'Configure Columns', icon: FiSettings },
  {
    id: 'client_processing',
    label: 'Process & Encrypt',
    icon: FiChevronsRight,
  },
  { id: 'finalize_actions', label: 'Finalize Actions', icon: FiSave },
  { id: 'workflow_error', label: 'Error', icon: FiXCircle },
];

const ALLOWED_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/csv',
];

function PrepareDataPage() {
  const navigate = useNavigate();
  const { dataSourceTypeName, batchId } = useParams();
  const { selectedProjectId } = useOutletContext();
  const queryClient = useQueryClient();

  const [isElectron, setIsElectron] = useState(false);
  const [currentDataSourceType, setCurrentDataSourceType] = useState(null);

  const [currentStep, setCurrentStep] = useState('initial_loading');
  const [ephemeralHashingKey, setEphemeralHashingKey] = useState(null);
  const [outputEncryptionKey, setOutputEncryptionKey] = useState(null);
  const [isMfaError, setIsMfaError] = useState(false);

  // State for multiple files
  const [selectedRawFiles, setSelectedRawFiles] = useState([]);
  const [fileDetails, setFileDetails] = useState([]);

  const [rawFileHeaders, setRawFileHeaders] = useState([]);
  const [columnConfig, setColumnConfig] = useState(null);
  const [processedEncryptedBlob, setProcessedEncryptedBlob] = useState(null);
  const [clientAppManifest, setClientAppManifest] = useState(null);

  const [isProcessing, setIsProcessing] = useState(true);
  const [statusMessage, setStatusMessage] = useState('Initializing...');
  const [errorMessage, setErrorMessage] = useState('');
  const [operationMessage, setOperationMessage] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    setIsElectron(!!window.electronAPI);
    trackEvent('PrepareDataPage_Load', {
      isElectron: !!window.electronAPI,
      dataSource: dataSourceTypeName,
    });
  }, [dataSourceTypeName]);

  useEffect(() => {
    const initializeWorkflow = async () => {
      setIsProcessing(true);
      setCurrentStep('initial_loading');
      setStatusMessage('Requesting secure session...');

      if (!selectedProjectId || !dataSourceTypeName || !batchId) {
        setErrorMessage(
          'Project, Data Source, or Batch context is missing. Please return to the workspace.',
        );
        setCurrentStep('workflow_error');
        setIsProcessing(false);
        return;
      }

      trackEvent('PrepareDataPage_Initialize', {
        projectId: selectedProjectId,
        dataSource: dataSourceTypeName,
        batchId,
      });

      try {
        const [secureConfig, tokenResponse] = await Promise.all([
          api.getSecureClientConfig(selectedProjectId),
          api.requestHashingKeyForBatch(selectedProjectId, batchId),
        ]);

        const fetchedDataSourceType = secureConfig.dataSourceTypeDetails?.find(
          (dst) => dst.name === dataSourceTypeName,
        );
        const fetchedHashingKey = tokenResponse.ephemeral_key;
        const fetchedOutputKey = secureConfig.OUTPUT_ENCRYPTION_KEY;

        if (!fetchedHashingKey || !fetchedDataSourceType || !fetchedOutputKey) {
          throw new Error(
            `Failed to initialize. Could not retrieve critical configuration (hashing key, data source type, or encryption key).`,
          );
        }

        setEphemeralHashingKey(fetchedHashingKey);
        setOutputEncryptionKey(fetchedOutputKey);
        setCurrentDataSourceType(fetchedDataSourceType);

        setCurrentStep('select_file');
        setStatusMessage(
          'Secure session established. Please select your data file(s).',
        );
        trackEvent('PrepareDataPage_InitializeSuccess');
      } catch (error) {
        if (
          error.status === 403 &&
          error.message &&
          error.message.includes('MFA')
        ) {
          setErrorMessage(error.message);
          setIsMfaError(true);
          setCurrentStep('workflow_error');
        } else {
          setErrorMessage(
            error.message ||
              'Failed to establish a secure session with the server.',
          );
          setIsMfaError(false);
          setCurrentStep('workflow_error');
        }
        trackEvent('PrepareDataPage_InitializeError', {
          reason: error.message,
        });
      } finally {
        setIsProcessing(false);
      }
    };
    initializeWorkflow();
  }, [selectedProjectId, dataSourceTypeName, batchId]);

  const handleFilesSelected = async (event) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (fileInputRef.current) fileInputRef.current.value = null;
    if (files.length === 0) return;

    for (const file of files) {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        const errorMsg = `Invalid file type detected: '${file.name}'. Please select only valid CSV or Excel (.xlsx, .xls) files.`;
        setErrorMessage(errorMsg);
        trackEvent('PrepareDataPage_FileSelectError', {
          reason: 'InvalidMIMEType',
          type: file.type,
        });
        return;
      }
    }

    trackEvent('PrepareDataPage_FilesSelected', {
      count: files.length,
      fileNames: files.map((f) => f.name),
    });
    setSelectedRawFiles(files);
    setErrorMessage('');
    setOperationMessage('');
    setIsProcessing(true);
    setStatusMessage(`Parsing and hashing ${files.length} file(s)...`);

    try {
      const details = await Promise.all(
        files.map(async (file) => {
          const [parsedResult, fileHash] = await Promise.all([
            parseSelectedFile(file),
            calculateFileHash(file),
          ]);
          return { file, hash: fileHash, headers: parsedResult.meta.fields };
        }),
      );

      const firstFileHeaders = details[0]?.headers || [];
      if (
        details.some(
          (d) => JSON.stringify(d.headers) !== JSON.stringify(firstFileHeaders),
        )
      ) {
        notify.warn(
          "Files have different column structures. Using the first file's columns for configuration.",
        );
        trackEvent('PrepareDataPage_InconsistentHeadersWarning');
      }

      setFileDetails(details);
      setRawFileHeaders(firstFileHeaders);

      setColumnConfig({
        ...currentDataSourceType,
        piiColumnsToRemove: currentDataSourceType.default_pii_columns || [],
        analyticalColumnsToKeep:
          currentDataSourceType.default_analytical_columns || [],
        icColumnName: currentDataSourceType.default_ic_column_name || '',
      });

      setOperationMessage(
        `${files.length} file(s) selected and analyzed. Ready for column configuration.`,
      );
      setCurrentStep('configure_columns');
      trackEvent('PrepareDataPage_FilesParseSuccess', { count: files.length });
    } catch (error) {
      setErrorMessage(error.message || 'Could not process the selected files.');
      setCurrentStep('workflow_error');
      trackEvent('PrepareDataPage_FilesParseError', { reason: error.message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfigSubmit = useCallback((confirmedConfig) => {
    trackEvent('PrepareDataPage_ConfigSubmit');
    setColumnConfig(confirmedConfig);
    setCurrentStep('client_processing');
    setOperationMessage('');
  }, []);

  const handleProcessingComplete = useCallback((processedData, manifest) => {
    trackEvent('PrepareDataPage_ProcessingSuccess', {
      deidentifiedRows: manifest.deidentified_rows,
      skippedRows: manifest.skipped_rows,
    });
    setProcessedEncryptedBlob(processedData.encryptedDataBlob);
    setClientAppManifest(manifest);
    setCurrentStep('finalize_actions');
  }, []);

  const handleProcessingError = useCallback((errorInfo) => {
    trackEvent('PrepareDataPage_ProcessingError', {
      reason: errorInfo.message,
    });
    setErrorMessage(errorInfo.message);
    setCurrentStep('workflow_error');
  }, []);

  const handleFinalizeAction = async (actionType) => {
    setIsProcessing(true);
    setErrorMessage('');
    setStatusMessage('Initiating final step...');
    trackEvent('PrepareDataPage_FinalizeStart', { action: actionType });

    try {
      if (actionType === 'saveLocally') {
        setStatusMessage('Saving file locally...');
        if (!isElectron)
          throw new Error('Local save is only supported in the Electron app.');

        const saveResult = await window.electronAPI.invoke('save-file-dialog', {
          fileContent: new Uint8Array(
            await processedEncryptedBlob.arrayBuffer(),
          ),
          defaultFilename: clientAppManifest.encrypted_filename_on_disk,
        });

        if (!saveResult.success) {
          setIsProcessing(false);
          setOperationMessage('Local save cancelled.');
          trackEvent('PrepareDataPage_LocalSaveCancelled');
          return;
        }
        setOperationMessage(
          `File successfully saved to ${saveResult.path}. You must still register it to complete the submission.`,
        );
        setIsProcessing(false);
        trackEvent('PrepareDataPage_LocalSaveSuccess', {
          path: saveResult.path,
        });
        return;
      }

      if (actionType === 'uploadAndRegister') {
        setStatusMessage('Requesting secure upload authorization...');
        const uploadUrlResponse = await api.requestUploadUrl(
          selectedProjectId,
          clientAppManifest.encrypted_filename_on_disk,
          currentDataSourceType.name,
        );

        setStatusMessage('Uploading encrypted data...');
        await api.uploadFileDirectly(
          uploadUrlResponse.upload_url,
          processedEncryptedBlob,
          (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setStatusMessage(
              `Uploading encrypted data... ${percentCompleted}%`,
            );
          },
        );
      }

      setStatusMessage('Registering file metadata...');
      await api.registerProcessedFileMetadata(
        selectedProjectId,
        batchId,
        clientAppManifest,
      );
      await queryClient.invalidateQueries({
        queryKey: ['jobs', selectedProjectId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['jobDetails', batchId],
      });

      trackEvent('PrepareDataPage_RegistrationSuccess', { action: actionType });
      navigate(`/user/workspace/${batchId}/overview`, {
        // Navigate back to the specific job
        state: {
          message: `Successfully prepared and registered data from ${clientAppManifest.original_filenames.length} file(s).`,
        },
      });
    } catch (err) {
      setErrorMessage(err.message || 'Failed to finalize the operation.');
      setCurrentStep('workflow_error');
      trackEvent('PrepareDataPage_FinalizeError', {
        reason: err.message,
        action: actionType,
      });
      setIsProcessing(false);
    }
  };

  const handleReset = () => navigate('/user/workspace');

  const renderStepContent = () => {
    if (isProcessing) {
      return (
        <Box
          className={styles.stepContentCentered}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flexGrow={1}
          py="xl"
        >
          <Spinner size="lg" />
          <Typography variant="h4" mt="md" textAlign="center">
            {statusMessage}
          </Typography>
        </Box>
      );
    }

    switch (currentStep) {
      case 'select_file':
        return (
          <Box py="xl" textAlign="center">
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              leftIcon={<Icon as={FiFolder} />}
              size="lg"
            >
              Select File(s)
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFilesSelected}
              multiple
              style={{ display: 'none' }}
              accept=".csv,.xlsx,.xls"
            />
          </Box>
        );

      case 'configure_columns':
        const fileNames = selectedRawFiles.map((f) => f.name).join(', ');
        return (
          <ColumnConfigurationStep
            allColumns={rawFileHeaders}
            initialConfig={{
              ...columnConfig,
              fileObject: selectedRawFiles[0],
              allFileNames: fileNames,
            }}
            onConfigSubmit={handleConfigSubmit}
            onBack={() => {
              setCurrentStep('select_file');
              setSelectedRawFiles([]);
              setFileDetails([]);
              setOperationMessage('');
            }}
            dataSourceType={currentDataSourceType}
          />
        );

      case 'client_processing':
        return (
          <ProcessingStep
            fileDetails={fileDetails}
            config={{
              ...columnConfig,
              sourceType: dataSourceTypeName,
              icColumnName:
                columnConfig?.icColumnName ||
                currentDataSourceType?.default_ic_column_name,
            }}
            appConfig={{
              projectId: selectedProjectId,
              hashingKey: ephemeralHashingKey,
              outputEncryptionKey: outputEncryptionKey,
              batchId: batchId,
            }}
            onSuccess={handleProcessingComplete}
            onError={handleProcessingError}
            disabled={isProcessing}
          />
        );

      case 'finalize_actions':
        const originalFileNames =
          clientAppManifest?.original_filenames?.join(', ') || 'your file(s)';
        return (
          <Stack spacing="lg" mt="lg">
            <Alert
              variant="success"
              title={`Processing Complete: ${originalFileNames}`}
            >
              {`Local processing complete. ${clientAppManifest?.deidentified_rows} records have been deidentified and encrypted. Please review the summaries below before submitting to the platform.`}
            </Alert>

            {clientAppManifest && (
              <ResultsStep manifest={clientAppManifest} isEmbedded={true} />
            )}
            {clientAppManifest && (
              <TrustAndSecurityCard manifest={clientAppManifest} />
            )}

            <HStack spacing="md" justifyContent="center" mt="md">
              <Tooltip content="Uploads the encrypted file to secure storage and registers its metadata with the central server. This is the standard final step.">
                <Button
                  variant="primary"
                  onClick={() => handleFinalizeAction('uploadAndRegister')}
                  leftIcon={<Icon as={FiUploadCloud} />}
                >
                  Register File with Batch #{batchId}
                </Button>
              </Tooltip>
              {isElectron && (
                <Tooltip content="Saves the encrypted file to your local machine for inspection or archival. You must still register the file with the platform to complete the submission.">
                  <Button
                    variant="secondary"
                    onClick={() => handleFinalizeAction('saveLocally')}
                    leftIcon={<Icon as={FiSave} />}
                  >
                    Save Encrypted File Locally
                  </Button>
                </Tooltip>
              )}
            </HStack>
          </Stack>
        );

      case 'workflow_error':
        if (isMfaError) {
          return (
            <Stack alignItems="center" spacing="lg" py="xl">
              <Alert
                variant="error"
                title="MFA Required"
                icon={<Icon as={FiShield} />}
              >
                {errorMessage}
              </Alert>
              <Button
                variant="primary"
                onClick={() => navigate('/user/account')}
                leftIcon={<Icon as={FiShield} />}
              >
                Go to Security Settings
              </Button>
            </Stack>
          );
        }

        return (
          <Stack alignItems="center" spacing="md" py="xl">
            <Alert variant="error" title="Workflow Error">
              {errorMessage}
            </Alert>
            <Button variant="outline" onClick={handleReset}>
              Return to Workspace
            </Button>
          </Stack>
        );

      default:
        return (
          <Box className={styles.stepContentCentered}>
            <Spinner size="lg" />
            <Typography mt="md">{statusMessage}</Typography>
          </Box>
        );
    }
  };

  const currentStepInfo = STEPS.find((s) => s.id === currentStep) || STEPS[0];

  return (
    <Card
      title={
        <HStack spacing="sm" alignItems="center">
          <Icon
            as={currentStepInfo.icon || FiUploadCloud}
            size="lg"
            color="primary"
          />
          <Typography variant="h3" component="h2" m={0}>
            Prepare Data: {currentDataSourceType?.display_name || '...'}
          </Typography>
        </HStack>
      }
    >
      <Stack spacing="lg" p="md">
        {operationMessage && (
          <Alert variant="info" onDismiss={() => setOperationMessage('')}>
            {operationMessage}
          </Alert>
        )}
        <Typography variant="body2" color="secondary">
          Follow the steps to securely pseudonymize your data for the{' '}
          <strong>{currentDataSourceType?.display_name || ''}</strong> category.
          Raw PII is processed locally and never leaves your machine.
        </Typography>
        <Stepper
          steps={STEPS.filter((s) => s.id !== 'workflow_error')}
          activeStepId={currentStep}
        />
        <div className={styles.stepContentArea}>{renderStepContent()}</div>
        {currentStep !== 'workflow_error' &&
          currentStep !== 'initial_loading' && (
            <HStack
              justifyContent="flex-end"
              pt="md"
              borderTop="1px solid var(--cipher-color-border-subtle)"
              mt="lg"
            >
              <Button
                variant="outline"
                onClick={() => navigate(`/user/workspace`)}
                leftIcon={<Icon as={FiArrowLeft} />}
                disabled={isProcessing}
              >
                Back to Workspace
              </Button>
            </HStack>
          )}
      </Stack>
    </Card>
  );
}

export default PrepareDataPage;
